import { getOrCreateKeyPairSafe } from '@/lib/crypto.migrationWrapper';
import { getOrCreateSigningKeyPair } from '@/lib/signing';
import { registerSigningPublicKey, exportSigningPublicKey } from '@/lib/publicKeyRegistry';
import { registerDevice } from '@/lib/deviceIdentity';
import { logger } from '@/lib/logger';

/**
 * Sets up encryption keys, signing keys, and device registration.
 * Returns the private encryption key.
 * All signing/device operations are non-critical and fully isolated:
 * any failure here MUST NOT break auth hydration.
 */
export async function setupKeysAndDevice(userId: string): Promise<CryptoKey> {
  // Encryption key pair is critical — let it surface if it truly fails,
  // but isolate the signing path entirely.
  const encResult = await getOrCreateKeyPairSafe(userId);

  // Best-effort: signing key + registration + device. Never throw out of here.
  (async () => {
    let sigResult: { publicKey?: CryptoKey } | null = null;
    try {
      sigResult = await getOrCreateSigningKeyPair(userId);
    } catch (e) {
      logger.warn('Signing key generation failed:', e);
      return;
    }

    if (!sigResult?.publicKey) return;

    try {
      await registerSigningPublicKey(userId, sigResult.publicKey);
    } catch (e) {
      logger.warn('Signing key registration failed:', e);
    }

    try {
      const exported = await exportSigningPublicKey(sigResult.publicKey);
      await registerDevice(userId, exported);
    } catch (e) {
      logger.warn('Device registration failed:', e);
    }
  })().catch((e) => logger.warn('signingSetup background task failed:', e));

  return encResult.privateKey;
}
